import { PageHero } from '@/components/PageHero';
import { useSeo } from '@/seo/useSeo';

export function Privacy(): JSX.Element {
  useSeo({
    title: 'Privacy policy',
    description:
      'What this site collects (an email address, only if you submit one), how it\'s used, and what it doesn\'t collect — no cookies, no analytics, no third-party trackers.',
    path: '/privacy',
  });

  return (
    <div>
      <PageHero
        eyebrow="Legal"
        title="Privacy policy"
        subtitle="Plain-language, because that's the standard we hold the rest of this site to as well. Effective July 13, 2026."
      />

      <section className="pb-16 md:pb-24">
        <article className="container-content max-w-[720px]">
          <p className="text-[15px] leading-relaxed text-white/75">
            This page covers nexusrmm.ai, the marketing site you&apos;re on now. It does not cover
            the Nexus product itself once you&apos;re a design partner and running real client data
            through it — that&apos;s a separate agreement we&apos;ll walk through directly as part of
            onboarding, scoped to what your tenant actually stores.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">
            What this site collects
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            The only personal information this site collects is an email address — and only if you
            submit one through the &quot;Join private preview&quot; form. We don&apos;t collect names,
            phone numbers, company details, or anything else through this site unless you send it to
            us directly by email.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">
            What this site doesn&apos;t collect
          </h2>
          <ul className="mt-4 flex flex-col gap-2.5">
            <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
              <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
              No cookies are set by this site.
            </li>
            <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
              <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
              No analytics or tracking scripts run on this site — no Google Analytics, no ad pixels,
              no third-party trackers of any kind.
            </li>
            <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
              <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
              Nothing is sold, rented, or shared with a third party for marketing purposes — full
              stop.
            </li>
          </ul>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">
            How your email is used
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            If you submit your email through the notify form, it&apos;s stored as a lead record in
            our own platform (the same product this site markets) and used to reach out about the
            design-partner cohort and relevant product updates. You can ask us to remove it at any
            time — email{' '}
            <a href="mailto:hello@nexusrmm.ai" className="text-nexus-purple-light no-underline hover:underline">
              hello@nexusrmm.ai
            </a>{' '}
            and we&apos;ll delete it, no forms or friction.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">
            Children&apos;s privacy
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            This site is directed at MSP owners and operators, not children, and we don&apos;t
            knowingly collect information from anyone under 13. (Separately, the Nexus product
            itself tracks COPPA-relevant posture for clients whose own end users may include
            minors — see the{' '}
            <a href="/compliance/ferpa-coppa" className="text-nexus-purple-light no-underline hover:underline">
              FERPA/COPPA compliance page
            </a>{' '}
            for that, which is a different topic from this site&apos;s own data collection.)
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">Changes</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            If this policy changes in a way that matters, we&apos;ll update the effective date above.
            We&apos;re not going to backdate a change or announce one quietly.
          </p>

          <h2 className="mt-10 font-display text-xl font-bold text-nexus-ink md:text-2xl">Contact</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/75">
            Questions about this policy, or a request to delete data we hold on you:{' '}
            <a href="mailto:hello@nexusrmm.ai" className="text-nexus-purple-light no-underline hover:underline">
              hello@nexusrmm.ai
            </a>
            .
          </p>
        </article>
      </section>
    </div>
  );
}
